/* eslint-disable max-statements */
import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import Pagination from '../pagination';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSelectedSection } from '../../../redux/actions/sideBarAction';
import TableLoadingSkeleton from '../../Utils/LoadingAnimations/TableLoadingSkeleton';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import { useTableQueryState } from '../../../hooks/useTableQueryState';
import LazyImage from './Components/imageComponent';
import { globalEnv } from '../../../config/baseConfig';
import {
	setSearchPlaceholderAction,
	setSearchQueryAction
} from '../../../redux/actions/searchBarAction';
import { SET_SEARCH_INPUT_VALUE } from '../../../redux/types/types';
import { HeaderItem } from './Components/common';
import { DataNotFoundAnimation } from '../../Utils/ErrorAnimations/dataNotFoundAnim';
import { axiosInstance } from '../../../config/axiosInstance';
import { AxiosRequestConfig } from 'axios';

export default function UserTable({
	isActive,
	isPremium
}: {
	isActive?: boolean;
	isPremium?: boolean;
}) {
	const queryClient = useQueryClient();
	const tableKey = isPremium
		? 'users-premium'
		: isActive == null
		? 'users-all'
		: isActive
		? 'users-active'
		: 'users-inactive';
	// List state lives in URL search params so that navigating to a user and
	// coming back restores the previous sort/page/search.
	const {
		page,
		selectedHeader,
		sortOrder,
		urlFilter,
		setPage,
		setSelectedHeader,
		setSortOrder,
		setFilter
	} = useTableQueryState({ tableKey, defaultSortBy: 'lastActive' });
	const [usersCount, setUsersCount] = useState(0);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);
	const { searchQuery }: { searchQuery: string } = useTypedSelector(
		(state) => state.searchbar
	);

	const dispatch = useDispatch();

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});
	useEffect(() => {
		dispatch(setSelectedSection('User Documents'));
		// Resets the search bar; when returning from a detail page, restore the
		// previous search from the URL so the query is preserved.
		dispatch(setSearchPlaceholderAction('Search for Users'));
		if (urlFilter) {
			dispatch({ type: SET_SEARCH_INPUT_VALUE, name: urlFilter });
			dispatch(setSearchQueryAction(urlFilter));
		}

		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	// Mirror search bar changes into the URL (skipping the initial render,
	// which is handled by the restore above). The urlFilter guard prevents
	// re-writing identical params when only the setter identity changed.
	const isFirstSearchSync = useRef(true);
	useEffect(() => {
		if (isFirstSearchSync.current) {
			isFirstSearchSync.current = false;
			return;
		}
		if (searchQuery !== urlFilter) setFilter(searchQuery);
	}, [searchQuery, urlFilter, setFilter]);

	const { data, isError, isFetching, isLoading } = useQuery(
		[isActive, isPremium, page, selectedHeader, sortOrder, searchQuery],
		() => fetchUsers(page),

		{ keepPreviousData: true, staleTime: 5000, refetchOnWindowFocus: false }
	);

	// Prefetch the next page!
	useEffect(() => {
		if (data?.hasMore) {
			queryClient.prefetchQuery(
				[isActive, isPremium, page + 1, selectedHeader, sortOrder, searchQuery],
				() => fetchUsers(page + 1)
			);
		}
	}, [data, page, queryClient]);

	useEffect(() => {
		if (data != null) {
			setUsersCount(data.meta.total);
			console.log(data.meta.total);
		}
	}, [data]);

	useEffect(() => {
		const intervalId = setInterval(() => {
			window.location.reload();
		}, 1000 * 60 * 20);
		return () => clearInterval(intervalId);
	}, []);

	async function fetchUsers(page: number) {
		const config: AxiosRequestConfig = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};

		let urlSection = 'get-all';

		if (isActive != null) {
			urlSection = isActive ? 'get-active-users' : 'get-inactive-users';
		}

		if (isPremium != null && isPremium === true) {
			urlSection = 'get-premium-users';
		}

		const { data } = await axiosInstance.get(
			globalEnv.BASE_URL +
				`/admin/user/${urlSection}?page=${page}&sortby=${selectedHeader}&order=${sortOrder}${
					searchQuery ? '&filter=' + encodeURIComponent(searchQuery) : ''
				}`,
			config
		);
		return data;
	}

	// Ordered ids of the currently visible page, passed to the detail page so
	// it can offer previous/next navigation between users.
	const userIds: string[] = data?.users?.map((u: any) => u._id) ?? [];

	const TableHeaders = () => {
		return (
			<tr>
				<HeaderItem
					name="Username"
					slugName="username"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Email"
					slugName="email"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<HeaderItem
					name="Signup Date"
					slugName="createdAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<HeaderItem
					name="Signup Process"
					slugName="isSocial"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<HeaderItem
					name="Last Active"
					slugName="lastActive"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<th className="p-2 whitespace-nowrap">
					<div className="font-semibold text-center">Role(s)</div>
				</th>

				<HeaderItem
					name=""
					slugName=""
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<th className="absolute p-2 whitespace-nowrap right-0">
					<div className="font-semibold text-left">
						{`Count: ${usersCount}`}
					</div>
				</th>
			</tr>
		);
	};

	return (
		<section>
			<div className="p-3">
				<div>
					<div className="overflow-x-auto ">
						<table className="table-auto w-full">
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<thead className="text-xs font-bold uppercase dark:text-primary-text-dark text-primary-text-light">
									<TableHeaders />
								</thead>
							)}
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
									{data.users.length > 0 &&
										data.users.map((item: any, index: number) => (
											<TableData
												key={item._id}
												id={item._id}
												username={item.username}
												email={item.email}
												signupDate={item.createdAt}
												isSocial={item.isSocial}
												lastActive={item.lastActive}
												roles={item.roles}
												photo={
													item.photo != null
														? item.photo
														: `https://api.dicebear.com/7.x/bottts/svg?seed=${item.username}`
												}
												userIds={userIds}
												index={index}
											/>
										))}
								</tbody>
							)}
						</table>
					</div>
				</div>
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : isError ? (
					<div className="h-full ml-0 ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : (
					<Pagination
						itemsPerPage={data.meta.limit}
						totalCount={data.meta.total}
						totalPages={data.meta.pages}
						currentPage={page}
						setPage={setPage}
					/>
				)}
			</div>

			{/* <ReactQueryDevtools initialIsOpen /> */}
		</section>
	);
}

type tableDateType = {
	id: string;
	username: string;
	email: string;
	signupDate: string;
	isSocial?: boolean;
	lastActive: string;
	roles: string[];
	photo: string;
	userIds: string[];
	index: number;
};

const TableData = ({
	id,
	username,
	email,
	signupDate,
	isSocial,
	lastActive,
	roles,
	photo,
	userIds,
	index
}: tableDateType) => {
	const navigate = useNavigate();
	return (
		<tr
			className="cursor-pointer"
			onClick={() => {
				navigate(`../user/${id}`, {
					state: { fromList: true, userIds, userIndex: index }
				});
			}}
		>
			<td className="w-3/12 md:w-4/12 p-2 break-all">
				<div className="flex items-center">
					<div className="w-10 h-10 flex justify-center flex-shrink-0 mr-2 sm:mr-3">
						<LazyImage photo={photo} />
					</div>
					<div className="font-medium dark:text-primary-text-dark text-primary-text-light">
						{username}
					</div>
				</div>
			</td>
			<td className=" w-3/12 md:w-3/12 p-2 break-all">
				<div className="text-left dark:text-secondary-text-dark text-secondary-text-light font-medium">
					{email}
				</div>
			</td>
			<td className="md:w-2/12 p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-secondary-text-dark text-secondary-text-light">
					{moment(signupDate).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>
			<td className="md:w-1/12 p-2 whitespace-nowrap">
				<div className="flex flex-row justify-center">
					{isSocial == null ? (
						<div className="text-center font-medium dark:text-secondary-text-dark text-secondary-text-light">
							N/A
						</div>
					) : (
						<div
							className={
								'flex justify-center items-center m-1 py-1.5 px-1 rounded-full font-semibold transition-all duration-500 ' +
								(isSocial ? 'bg-blue-700' : 'bg-gray-700')
							}
						>
							<div className="text-xs text-primary-text-dark font-semibold mx-1">
								{isSocial ? 'GOOGLE' : 'EMAIL'}
							</div>
						</div>
					)}
				</div>
			</td>
			<td className="md:w-2/12 p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-secondary-text-dark text-secondary-text-light">
					{lastActive != null
						? moment(lastActive).format('MM/DD/YYYY - HH:mm')
						: 'N/A'}
				</div>
			</td>
			<td className="md:w-2/12 w-2/12 md:p-2 md:whitespace-nowrap">
				<div className="flex flex-row justify-center">
					{roles.map((tag, index) => {
						return (
							<div
								key={index}
								className={
									'flex justify-center items-center m-1 py-1.5 px-1 rounded-full dark:text-primary-text-dark text-primary-text-light font-semibold  transition-all duration-500 ' +
									(tag === 'admin'
										? 'bg-green-700 border-green-600'
										: tag === 'user'
										? 'bg-gray-700 border-blue-600'
										: 'bg-yellow-700 border-yellow-600')
								}
							>
								<div className="text-xs text-primary-text-dark font-semibold mx-1">
									{tag.toUpperCase()}
								</div>
							</div>
						);
					})}
				</div>
			</td>
		</tr>
	);
};
